import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  PlusIcon,
  Eye,
  Edit,
  Calendar,
  Pause,
  Play,
  Camera,
  BookOpen,
  Smartphone,
  Trash2,
  FilterX,
  Activity,
  CheckCircle,
  Clock,
  BookCopy,
  Trash,
} from "lucide-react";

import { Button } from "@shared/ui/button";
import { Card, CardContent } from "@shared/ui/card";
import { useToast } from "@shared/hooks/use-toast";
import {
  FieldTooltip,
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { GenericTable } from "@shared/components/GenericTable";
import { DynamicDashboard } from "@features/mx/shared/components/dynamicDashboard";
import { TableAction, TableColumn } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  useGetAllMeterCycle,
  useGetReadCycleSummary,
  useMeterCycleExport,
  usePauseReadCycle,
} from "../../hooks";
import { PAGE_SIZE } from "@shared/utils/constants";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useChoices } from "@features/mx/device-management/hooks";
import { mapToLabelValue } from "@features/mx/device-management/utils";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Dashboard } from "../dashboard/dashboard";
import { PageHeader } from "@shared/components/PageHeader";
import { ReadCycleFiltersPanel } from "./filter";

const CreateReadCycleButton = () => {
  const navigate = useNavigate();

  const handleCreateReadCycle = () => {
    navigate("/mx/read-cycle/add");
  };

  return (
    <Button onClick={handleCreateReadCycle}>
      <PlusIcon className="h-4 w-4 mr-2" />
      Create Read Cycle
    </Button>
  );
};

interface ReadCycleData {
  id: string;
  meterCycleName: string;
  meterTypeDisplay: "Manual" | "Photo" | "Smart";
  routes: number;
  meters: number;
  consumers: number;
  completionPercentage: number;
  createdOn: string;
  createdBy: string;
  runCount: number;
  nextRunDate: string;
  status: "Scheduled" | "In Progress" | "Completed" | "Delayed";
  routesName: string[];
  summary: {
    premiseCount: number;
    areaCount: number;
    subAreaCount: number;
    totalMeterCount: number;
  };
}

interface ReadCycleFilters {
  meterType: string;
  status: string;
}

const ReadCycleList: React.FC = () => {
  const navigate = useNavigate();
  const utilityId = useRemoteUtilityId();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Read all params from URL including page
  const searchQuery = decodeURIComponent(searchParams.get("search") || "");
  const meterType = searchParams.get("meterType") || "";
  const status = searchParams.get("status") || "";
  const currentPage = parseInt(searchParams.get("page") || "1");

  // pendingFilters: UI state for user edits
  const [pendingFilters, setPendingFilters] = useState<ReadCycleFilters>({
    meterType: decodeURIComponent(meterType),
    status: decodeURIComponent(status),
  });

  const statusList = [
    { label: "Scheduled", value: "scheduled" },
    { label: "Unscheduled", value: "unscheduled" },
  ];

  // sync pendingFilters when URL params change
  useEffect(() => {
    setPendingFilters({
      meterType: decodeURIComponent(meterType),
      status: decodeURIComponent(status),
    });
  }, [meterType, status]);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: PAGE_SIZE,
    startItem: 0,
    endItem: 0,
  });

  const queryParams = useMemo(
    () => ({
      page: currentPage.toString(),
      search: searchQuery,
      meterType: meterType,
      status: status,
      startDate: "",
      endDate: "",
    }),
    [currentPage, searchQuery, meterType, status]
  );

  const { data: meterTypes } = useChoices({ choice: "meter_type" });
  const meterTypeList = mapToLabelValue(meterTypes);
  const {
    data: meterCycleResponse,
    isLoading,
    refetch: refetchMeterCycles,
  } = useGetAllMeterCycle(queryParams);

  const { exportCSV, isExporting } = useMeterCycleExport(
    utilityId,
    searchQuery,
    meterType,
    status,
    "", // startDate
    "" // endDate
  );

  const pauseReadCycleMutation = usePauseReadCycle();

  // Update pagination state when API response changes
  useEffect(() => {
    if (meterCycleResponse) {
      const totalItems = meterCycleResponse.count || 0;
      const totalPages = Math.ceil(totalItems / PAGE_SIZE);
      const startItem = (currentPage - 1) * PAGE_SIZE + 1;
      const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

      setPagination({
        currentPage: currentPage,
        totalPages,
        totalItems,
        itemsPerPage: PAGE_SIZE,
        startItem: totalItems > 0 ? startItem : 0,
        endItem: totalItems > 0 ? endItem : 0,
      });
    }
  }, [meterCycleResponse, currentPage]);

  const handlePendingFilterChange = (
    key: keyof ReadCycleFilters,
    value: string
  ) => {
    setPendingFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      ...(pendingFilters.meterType && { meterType: pendingFilters.meterType }),
      ...(pendingFilters.status && { status: pendingFilters.status }),
    };

    setSearchParams(newParams);
  };

  const resetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };

    delete newParams.meterType;
    delete newParams.status;
    delete newParams.search;

    setSearchParams(newParams);
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      ...(meterType && { meterType }),
      ...(status && { status }),
    };

    if (searchTerm.trim()) {
      newParams.search = searchTerm.trim();
    } else {
      delete newParams.search; // remove from URL if search is empty
    }

    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: Record<string, string> = {
      ...Object.fromEntries(searchParams.entries()),
      page: page.toString(),
    };
    setSearchParams(newParams);
  };

  const columns: TableColumn<ReadCycleData>[] = [
    {
      key: "meterCycleName",
      header: "Read Cycle Name",
      sortable: true,
    },
    {
      key: "meterTypeDisplay",
      header: "Read Type",
      sortable: true,
      render: (value: string) => (
        <div className="flex items-center gap-2">
          {value === "Manual Meter" && (
            <BookOpen className="h-4 w-4 text-orange-500" />
          )}
          {value === "Photo Meter" && (
            <Camera className="h-4 w-4 text-blue-500" />
          )}
          {value === "Smart Meter" && (
            <Smartphone className="h-4 w-4 text-purple-500" />
          )}
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: "productCode",
      header: "Utility Service",
      sortable: true,
      render: (value: string, row: any) =>
        row?.productCode?.map((service: any, index: number) => (
          <span key={index}>
            {service}
            {index < row.productCode.length - 1 && ", "}
          </span>
        )),
    },
    {
      key: "routesName",
      header: "Routes",
      sortable: true,
      render: (value: string[]) => <span>{value?.length || 0}</span>,
    },
    {
      key: "totalMeterCount",
      header: "Meters",
      sortable: true,
      render: (value: any, row: ReadCycleData) => (
        <span>{row.summary?.totalMeterCount || 0}</span>
      ),
    },
    {
      key: "consumerCount",
      header: "Consumers",
      sortable: true,
    },
    {
      key: "",
      header: "Created On",
      sortable: true,
      render: (value: string, row: any) => (
        <div>
          <p className="font-medium">{row.createdDate ?? "NA"}</p>
          <p className="text-xs text-muted-foreground">
            {row.createdUserRemoteName ?? "NA"}
          </p>
        </div>
      ),
    },
    {
      key: "runCount",
      header: "Run Count",
      sortable: true,
    },
    {
      key: "nextRunDate",
      header: "Next Run Date",
      sortable: true,
    },
  ];

  const actions = [
    {
      label: "View",
      onClick: (row: ReadCycleData) => {
        navigate(
          `/mx/read-cycle/view/view-details/${row.id}/${row.meterCycleName}`
        );
      },
      icon: <Eye className="h-4 w-4 mr-2" />,
    },
    {
      label: "Edit",
      onClick: (row: ReadCycleData) => {
        navigate(`/mx/read-cycle/edit/${row.id}`);
      },
      icon: <Edit className="h-4 w-4 mr-2" />,
    },
  ];

  const filterPanel = (
    <ReadCycleFiltersPanel
      meterType={meterType}
      status={status}
      meterTypeList={meterTypeList}
      statusList={statusList}
      onApplyFilters={(filters) => {
        const newParams: any = {
          ...Object.fromEntries(searchParams.entries()),
          ...(filters.meterType && { meterType: filters.meterType }),
          ...(filters.status && { status: filters.status }),
        };
        setSearchParams(newParams);
      }}
      onResetFilters={resetFilters}
    />
  );

  const hasActiveFilters = meterType || status;

  const handleExport = () => {
    exportCSV();
  };

  return (
    <div className="space-y-6 p-1">
      {/* Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <PageHeader
            title="Read Cycles"
            subtitle="Manage meter reading cycles and schedules for accurate billing."
          />
        </div>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <CreateReadCycleButton />
            </TooltipTrigger>
            <FieldTooltip>
              Create a new read cycle to schedule when and how meters will be
              read
            </FieldTooltip>
          </Tooltip>
        </TooltipProvider>
      </div>
      <Dashboard />
      <div className="space-y-4">
        <GenericTable
          data={meterCycleResponse?.results ?? []}
          columns={columns}
          rowKey="id"
          selectable={false}
          actions={actions}
          actionsType="dropdown"
          emptyMessage="No read cycles found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by cycle name..."
          filterPanel={filterPanel}
          onExport={handleExport}
          exportLoading={isExporting}
          filters={
            hasActiveFilters ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {[!!meterType, !!status].filter(Boolean).length} filter(s)
                  active
                </span>
              </div>
            ) : null
          }
        />
      </div>
    </div>
  );
};

export default ReadCycleList;
